/**
 * DiagnosticReportPDF — the write-up a customer gets after a health check.
 *
 * Shares the header, footer and palette of ServiceHistoryPDF and InvoicePDF so
 * a workshop's paperwork reads as one family. What it is NOT is a quote: the
 * money on it is an estimate of putting the findings right, clearly labelled as
 * such, with no VAT breakdown, no balance due, no pay button and no bank
 * details. The customer decides what to do; the quote comes after.
 *
 * Findings are ordered critical → advisory → monitor, because the order a
 * mechanic enters them in is the order they looked at the car, not the order
 * that matters to the person reading it.
 */
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { PdfPageFooter } from '../../../../lib/PdfPageFooter';
import { isWhiteLabelTier } from '../../../../lib/tiers';
import { DEFAULT_DOCUMENT_ACCENT } from '../../../../lib/brandAccent';

import { DOC } from '../../../../lib/documentPalette';

export interface DiagnosticIdentity {
    name: string;
    logoBase64?: string;
    logo_url?: string;
    address?: string;
    phone?: string;
    email?: string;
    vat_number?: string;
    accent?: string;
    tier?: string | null;
}

export type DiagnosticSeverity = 'critical' | 'advisory' | 'monitor';

export interface DiagnosticPDFFinding {
    system: string;
    title: string;
    severity: DiagnosticSeverity;
    notes?: string;
    photoCount?: number;
}

export interface DiagnosticPDFLineItem {
    type?: string;
    description: string;
    quantity: number;
    unitPrice: number;
}

export interface DiagnosticReportPDFProps {
    identity: DiagnosticIdentity;
    customer: { full_name?: string; email?: string; phone?: string };
    vehicle: { vrm?: string | null; make?: string | null; model?: string | null; year?: number | string | null; mileageText?: string | null } | null;
    findings: DiagnosticPDFFinding[];
    lineItems: DiagnosticPDFLineItem[];
    summary?: string | null;
    reportDate?: string;
}

const C = DOC;

// Print-safe: a solid swatch plus the word. Colour alone fails on a mono
// printer and for the ~1 in 12 men who cannot separate red from green — and
// "which of these is the urgent one" is the entire point of the document.
const SEVERITY: Record<DiagnosticSeverity, { label: string; note: string; colour: string }> = {
    critical: {
        label: 'Needs attention now',
        note: 'We would not recommend leaving this.',
        colour: DOC.urgent,
    },
    advisory: {
        label: 'Advisory',
        note: 'Worth planning in — not urgent today.',
        colour: DOC.advisory,
    },
    monitor: {
        label: 'Keep an eye on it',
        note: "Noted so we can watch it at the next visit.",
        colour: DOC.ok,
    },
};

const ORDER: DiagnosticSeverity[] = ['critical', 'advisory', 'monitor'];

const s = StyleSheet.create({
    page: {
        fontFamily: 'Helvetica',
        fontSize: 9,
        lineHeight: 1.4,
        color: C.body,
        backgroundColor: C.white,
        paddingBottom: 40,
    },

    header: { padding: '24 35 18 35', borderBottom: `1px solid ${C.line}` },
    logoRow: { marginBottom: 16 },
    logoBox: { width: 48, height: 48 },
    logo: { width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 },

    row: { flexDirection: 'row', alignItems: 'flex-start' },
    colLeft: { flex: 1.4, flexShrink: 0, flexGrow: 1.4, paddingRight: 24 },
    colRight: { flex: 1, flexShrink: 0, flexGrow: 1, alignItems: 'flex-end' },

    label: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: C.label, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 6 },
    h1: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: C.black, marginBottom: 2 },
    sm: { fontSize: 8, color: C.muted, marginBottom: 1.5 },
    vat: { fontSize: 7, color: C.label, marginTop: 4 },

    docTitle: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: C.black, letterSpacing: 1.2, textTransform: 'uppercase', textAlign: 'right', marginBottom: 2 },
    docMeta: { fontSize: 8, color: C.muted, textAlign: 'right', marginBottom: 1 },

    twoCol: { flexDirection: 'row', padding: '12 35 0 35', marginTop: 12 },
    boxHalf: { flex: 1, paddingRight: 16 },
    customerName: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: C.black, marginBottom: 1 },

    summaryBox: {
        marginTop: 16,
        marginHorizontal: 35,
        padding: 12,
        backgroundColor: C.bg,
        borderRadius: 5,
        border: `1px solid ${C.line}`,
    },
    summaryText: { fontSize: 9, color: C.body },

    sectionHead: {
        marginTop: 18,
        marginHorizontal: 35,
        paddingBottom: 4,
        borderBottom: `1px solid ${C.line}`,
    },
    sectionTitle: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: C.black, letterSpacing: 0.5 },
    sectionSub: { fontSize: 7.5, color: C.muted, marginTop: 2 },

    groupHead: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 14,
        marginHorizontal: 35,
        paddingVertical: 5,
        paddingHorizontal: 8,
        backgroundColor: C.bg,
        borderRadius: 4,
    },
    swatch: { width: 8, height: 8, borderRadius: 2, marginRight: 7 },
    groupLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.black },
    groupNote: { fontSize: 7.5, color: C.muted, marginLeft: 8 },

    finding: { marginHorizontal: 35, paddingVertical: 8, borderBottom: `1px solid ${C.faintLine}` },
    findingTop: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' },
    findingTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.dark, flex: 1, paddingRight: 10 },
    findingSystem: { fontSize: 7.5, color: C.label, textTransform: 'uppercase', letterSpacing: 0.6 },
    findingNotes: { fontSize: 8, color: C.body, marginTop: 3 },
    findingPhotos: { fontSize: 7.5, color: C.label, marginTop: 3 },

    liRow: { flexDirection: 'row', justifyContent: 'space-between', marginHorizontal: 35, paddingVertical: 5, borderBottom: `1px solid ${C.faintLine}` },
    liDesc: { fontSize: 8.5, color: C.body, flex: 4, paddingRight: 10 },
    liQty: { fontSize: 8, color: C.muted, flex: 0.6, textAlign: 'right' },
    liTotal: { fontSize: 8.5, color: C.black, flex: 1.2, textAlign: 'right' },

    estimateBox: {
        marginTop: 12,
        marginHorizontal: 35,
        padding: 12,
        backgroundColor: C.bg,
        borderRadius: 5,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    estimateLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.black },
    estimateVal: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: C.black },

    caveat: { marginTop: 8, marginHorizontal: 35, fontSize: 7.5, color: C.muted, lineHeight: 1.5 },
    empty: { margin: 35, fontSize: 9, color: C.muted, textAlign: 'center' },
    thankYou: { padding: '18 35 10 35', textAlign: 'center', fontSize: 8, color: C.muted },
});

const fmt = (v: number) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v);
const fmtDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

export const DiagnosticReportPDF = ({
    identity,
    customer,
    vehicle,
    findings,
    lineItems,
    summary,
    reportDate,
}: DiagnosticReportPDFProps) => {
    const accent = identity.accent || DEFAULT_DOCUMENT_ACCENT;
    const whiteLabel = isWhiteLabelTier(identity.tier);
    const hasLogo = !!(identity.logoBase64 || identity.logo_url);

    const grouped = ORDER.map((sev) => ({
        severity: sev,
        items: findings.filter((f) => f.severity === sev),
    })).filter((g) => g.items.length > 0);

    const estimate = lineItems.reduce((sum, li) => sum + Number(li.quantity || 0) * Number(li.unitPrice || 0), 0);
    const vehicleName = [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(' ');
    const criticalCount = findings.filter((f) => f.severity === 'critical').length;

    return (
        <Document>
            <Page size="A4" style={s.page}>
                <PdfPageFooter reference={identity.name} branded={!whiteLabel} />

                <View style={{ height: 6, backgroundColor: accent }} />

                <View style={s.header}>
                    {hasLogo && (
                        <View style={s.logoRow}>
                            <View style={s.logoBox}>
                                <Image src={identity.logoBase64 || identity.logo_url} style={s.logo} />
                            </View>
                        </View>
                    )}

                    <View style={s.row}>
                        <View style={s.colLeft}>
                            <Text style={s.label}>Prepared By:</Text>
                            <Text style={s.h1}>{identity.name}</Text>
                            {identity.address ? <Text style={s.sm}>{identity.address.replace(/\\n/g, '\n')}</Text> : null}
                            {identity.phone ? <Text style={s.sm}>{identity.phone}</Text> : null}
                            {identity.email ? <Text style={s.sm}>{identity.email}</Text> : null}
                            {identity.vat_number ? <Text style={s.vat}>VAT: {identity.vat_number}</Text> : null}
                        </View>

                        <View style={s.colRight}>
                            <Text style={s.docTitle}>Vehicle Health Check</Text>
                            <Text style={s.docMeta}>{fmtDate(reportDate || new Date().toISOString())}</Text>
                            <Text style={s.docMeta}>
                                {findings.length} finding{findings.length === 1 ? '' : 's'}
                                {criticalCount > 0 ? ` · ${criticalCount} needing attention` : ''}
                            </Text>
                        </View>
                    </View>
                </View>

                <View style={s.twoCol}>
                    <View style={s.boxHalf}>
                        <Text style={s.label}>Customer:</Text>
                        <Text style={s.customerName}>{customer.full_name || 'Customer'}</Text>
                        {customer.email ? <Text style={s.sm}>{customer.email}</Text> : null}
                        {customer.phone ? <Text style={s.sm}>{customer.phone}</Text> : null}
                    </View>
                    <View style={s.boxHalf}>
                        <Text style={s.label}>Vehicle:</Text>
                        <Text style={s.customerName}>{vehicle?.vrm || 'Vehicle'}</Text>
                        {vehicleName ? <Text style={s.sm}>{vehicleName}</Text> : null}
                        {vehicle?.mileageText ? <Text style={s.sm}>Mileage: {vehicle.mileageText}</Text> : null}
                    </View>
                </View>

                {summary && summary.trim() ? (
                    <View style={s.summaryBox}>
                        <Text style={s.label}>In short</Text>
                        <Text style={s.summaryText}>{summary.trim()}</Text>
                    </View>
                ) : null}

                {findings.length === 0 ? (
                    <Text style={s.empty}>
                        We went over the vehicle and found nothing to report. Everything checked was within
                        tolerance.
                    </Text>
                ) : (
                    <>
                        <View style={s.sectionHead}>
                            <Text style={s.sectionTitle}>What we found</Text>
                            <Text style={s.sectionSub}>Most urgent first.</Text>
                        </View>

                        {grouped.map((group) => {
                            const meta = SEVERITY[group.severity];
                            return (
                                <View key={group.severity}>
                                    <View style={s.groupHead} wrap={false}>
                                        <View style={[s.swatch, { backgroundColor: meta.colour }]} />
                                        <Text style={s.groupLabel}>{meta.label}</Text>
                                        <Text style={s.groupNote}>{meta.note}</Text>
                                    </View>

                                    {group.items.map((f, i) => (
                                        <View key={i} style={s.finding} wrap={false}>
                                            <View style={s.findingTop}>
                                                <Text style={s.findingTitle}>{f.title || 'Finding'}</Text>
                                                <Text style={s.findingSystem}>{f.system}</Text>
                                            </View>
                                            {f.notes && f.notes.trim() ? (
                                                <Text style={s.findingNotes}>{f.notes.trim()}</Text>
                                            ) : null}
                                            {f.photoCount ? (
                                                <Text style={s.findingPhotos}>
                                                    {f.photoCount} photo{f.photoCount === 1 ? '' : 's'} on file — ask us and
                                                    we will send them over.
                                                </Text>
                                            ) : null}
                                        </View>
                                    ))}
                                </View>
                            );
                        })}
                    </>
                )}

                {lineItems.length > 0 && (
                    <>
                        <View style={s.sectionHead}>
                            <Text style={s.sectionTitle}>What it would cost to put right</Text>
                            <Text style={s.sectionSub}>An estimate, not a bill. Nothing is booked in.</Text>
                        </View>

                        {lineItems.map((li, i) => (
                            <View key={i} style={s.liRow} wrap={false}>
                                <Text style={s.liDesc}>{li.description || 'Item'}</Text>
                                <Text style={s.liQty}>
                                    {Number(li.quantity || 0)}
                                    {(li.type ?? '').toLowerCase() === 'labour' ? ' hr' : '×'}
                                </Text>
                                <Text style={s.liTotal}>{fmt(Number(li.quantity || 0) * Number(li.unitPrice || 0))}</Text>
                            </View>
                        ))}

                        <View style={s.estimateBox} wrap={false}>
                            <Text style={s.estimateLabel}>Estimated total</Text>
                            <Text style={s.estimateVal}>{fmt(estimate)}</Text>
                        </View>

                        {/* The number above is the single thing most likely to
                            cause an argument later, so what it does and does not
                            cover is stated on the document rather than left to
                            the conversation someone half-remembers. */}
                        <Text style={s.caveat}>
                            This is an estimate based on what we found today. Parts prices can move, and stripping a
                            job down occasionally uncovers something we could not see from the outside — if that
                            happens we will ring you before doing any further work. Nothing here is booked in and
                            nothing is charged until you say go ahead.
                        </Text>
                    </>
                )}

                <View style={s.thankYou}>
                    <Text>
                        Any questions about any of this, just reply to the email or give {identity.name} a ring — happy
                        to talk it through.
                    </Text>
                </View>
            </Page>
        </Document>
    );
};
